import { useMutation, useQueryClient } from '@tanstack/react-query';
import { publicSigningControllerSaveWitnessDetailsMutation } from '@dealstack/shared/lib/backendClient/@tanstack/react-query.gen';
import type { WitnessDetails } from './usePublicSigningInfo';

/**
 * Persist the witness's legal details via the typed backend client, then refresh
 * the signing-info query so the page advances from the details form to the
 * signing panel. The signing token travels in the body (read by the
 * public-signing middleware); the endpoint itself is unauthenticated.
 *
 * Wraps the generated `*Mutation` options so callers keep passing the plain
 * `WitnessDetails` form values — the token is merged into the request body here.
 */
export const useSaveWitnessDetails = (token: string) => {
	const queryClient = useQueryClient();
	const mutation = useMutation({
		...publicSigningControllerSaveWitnessDetailsMutation(),
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: ['public-signing-info', token] });
		},
	});

	return {
		mutate: (details: WitnessDetails, options?: Parameters<typeof mutation.mutate>[1]) =>
			mutation.mutate({ body: { token, ...details } }, options),
		isPending: mutation.isPending,
		isError: mutation.isError,
	};
};
